/**
 * Drill Mode Component
 * Handles the coding drill experience for students
 */
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { BookOpen, X, ChevronRight, ChevronLeft, Loader2, CheckCircle, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import QuestionCard from "./QuestionCard";
import CodeWorkspace from "./CodeWorkspace";
import { Blocks } from "lucide-react";
import DrillSuccessModal from "./DrillSuccessModal";
import TimerDisplay from "./Timer";
import { toast } from "sonner";
import { isBlockMode, MODE_META, modeLabel, normalizeMode } from "../lib/modes";

const API = "/api";

// Timer constants
const DRILL_TIMER_SECONDS = 3 * 60; // 3 minutes for blitz bonus
const BLITZ_BONUS_POINTS = 10;

export default function DrillMode({ 
  drill, 
  onExit, 
  user,
  onDrillComplete
}) {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [drillStatus, setDrillStatus] = useState("in_progress");
  const [completedCount, setCompletedCount] = useState(0);
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(DRILL_TIMER_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  
  // Streak tracking for this drill session
  const [streakCount, setStreakCount] = useState(0);
  
  // Ref for drill validity check interval
  const drillValidityCheckRef = useRef(null);
  
  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    startDrill();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (drillValidityCheckRef.current) {
        clearInterval(drillValidityCheckRef.current);
      }
    };
  }, []);

  // Periodic check to verify drill is still assigned (every 30 seconds)
  // Skip this check for admin preview mode
  useEffect(() => {
    // Skip validity check for admin preview
    if (drill.is_admin_preview) {
      return;
    }
    
    const checkDrillValidity = async () => {
      try {
        const response = await fetch(`${API}/user/assigned-drill`, {
          credentials: "include"
        });
        if (response.ok) {
          const data = await response.json();
          // Check this drill is still among the user's assigned drills
          // (must check the full list - system drills sort first as primary)
          const stillAssigned =
            (data.assigned_drills || []).some((d) => d.drill_id === drill.drill_id) ||
            data.assigned_drill?.drill_id === drill.drill_id;
          
          if (!stillAssigned) {
            toast.error("This drill has been unassigned or changed by your teacher.", {
              duration: 5000,
              description: "Your progress has been saved. Returning to main page."
            });
            onExit();
          }
        }
      } catch (error) {
        // Silently fail - don't interrupt user's work for network errors
      }
    };
    
    // Check every 30 seconds
    drillValidityCheckRef.current = setInterval(checkDrillValidity, 30000);
    
    return () => {
      if (drillValidityCheckRef.current) {
        clearInterval(drillValidityCheckRef.current);
      }
    };
  }, [drill.drill_id, drill.is_admin_preview, onExit]);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]);

  const startDrill = async () => {
    try {
      // Check if this is admin preview mode
      const isAdminPreview = drill.is_admin_preview === true;
      
      if (isAdminPreview) {
        // Admin preview - use the preview endpoint (no progress saved)
        const response = await fetch(`${API}/school/drill/${drill.drill_id}/preview`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          setQuestions(data.questions || []);
          setCurrentIndex(0);
          setCompletedCount(0);
          setDrillStatus("in_progress");
          
          // Start timer for current question
          setTimeRemaining(DRILL_TIMER_SECONDS);
          setTimerActive(true);
          
          toast.info("Preview Mode - Progress will not be saved", { duration: 4000 });
        } else {
          toast.error("Failed to load drill preview");
          onExit();
        }
      } else {
        // Normal student mode - use regular endpoints
        // Start/resume the drill
        await fetch(`${API}/user/drill/${drill.drill_id}/start`, {
          method: "POST",
          credentials: "include"
        });

        // Fetch drill questions
        const response = await fetch(`${API}/user/drill/${drill.drill_id}/questions`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          const loadedQuestions = data.questions || [];
          if (loadedQuestions.length === 0) {
            toast.error("This drill has no available questions.", {
              duration: 5000,
              description: "Please let your teacher know. Returning to main page."
            });
            onExit();
            return;
          }
          setQuestions(loadedQuestions);
          setCurrentIndex(data.current_index || 0);
          setCompletedCount(data.completed_count || 0);
          setDrillStatus(data.status);
          
          // Start timer for current question
          setTimeRemaining(DRILL_TIMER_SECONDS);
          setTimerActive(true);
        } else {
          toast.error("Failed to load drill questions");
          onExit();
        }
      }
    } catch (error) {
      console.error("Error starting drill:", error);
      toast.error("Error starting drill");
      onExit();
    } finally {
      setLoading(false);
    }
  };

  // Called by CodeEditor when answer is correct
  const handleCorrectAnswer = async (pointsEarned, alreadyAnswered, result) => {
    // Stop timer
    setTimerActive(false);
    
    const newStreakCount = streakCount + 1;
    setStreakCount(newStreakCount);
    
    // Check if this is admin preview mode
    const isAdminPreview = drill.is_admin_preview === true;
    
    // Trigger stats refresh in Navbar so user sees updated score
    if (!isAdminPreview) {
      window.dispatchEvent(new Event('refreshUserStats'));
    }
    
    // Mark question as completed in drill progress (skip for admin preview)
    if (!isAdminPreview) {
      try {
        const response = await fetch(`${API}/user/drill/${drill.drill_id}/complete-question/${currentQuestion.question_id}`, {
          method: "POST",
          credentials: "include"
        });
        
        // Check if drill was unassigned or changed while user was working
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 400 && errorData.detail === "Drill not started") {
            // Drill was unassigned/reset by teacher
            toast.error("This drill has been unassigned or reset by your teacher.", {
              duration: 5000,
              description: "Your answer was still correct! Returning to main page."
            });
            onExit();
            return;
          }
          // Log other errors but continue (points were already awarded by CodeEditor)
          console.error("Error marking question complete:", errorData);
        }
      } catch (error) {
        console.error("Error marking question complete:", error);
      }
    }
    
    const newCompletedCount = completedCount + 1;
    setCompletedCount(newCompletedCount);
    
    // Check if drill is complete
    const isDrillComplete = newCompletedCount >= questions.length;
    if (isDrillComplete) {
      setDrillStatus("completed");
    }
    
    // Show success modal - bonuses come from the grading response ONLY
    setLastResult({
      base_points: isAdminPreview ? 0 : (pointsEarned || 10),
      bonus_points: isAdminPreview ? 0 : ((result?.blitz_bonus || 0) + (result?.streak_bonus || 0) + (result?.gem_bonus || 0)),
      blitz_bonus: isAdminPreview ? 0 : (result?.blitz_bonus || 0),
      streak_bonus: isAdminPreview ? 0 : (result?.streak_bonus || 0),
      gem_bonus: isAdminPreview ? 0 : (result?.gem_bonus || 0),
      total_points: isAdminPreview ? 0 : (pointsEarned || 10),
      new_total: result?.new_total_score,
      streak_count: newStreakCount,
      is_drill_complete: isDrillComplete,
      is_admin_preview: isAdminPreview
    });
    setShowSuccess(true);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    
    if (lastResult?.is_drill_complete) {
      // Drill is complete
      toast.success("Congratulations! You completed the drill!");
      if (onDrillComplete) {
        onDrillComplete();
      }
      onExit();
    } else {
      // Move to next question
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      
      // Reset timer for next question
      setTimeRemaining(DRILL_TIMER_SECONDS);
      setTimerActive(true);
    }
  };

  const handleStopDrill = async () => {
    if (!window.confirm("Are you sure you want to stop the drill? Your progress will be saved and you can continue later.")) {
      return;
    }
    
    try {
      await fetch(`${API}/user/drill/${drill.drill_id}/stop`, {
        method: "POST",
        credentials: "include"
      });
      toast.info("Drill paused. You can continue later.");
    } catch (error) {
      console.error("Error stopping drill:", error);
    }
    
    onExit();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-orange-500 mb-4" />
          <p className="text-muted-foreground">Loading drill questions...</p>
        </div>
      </div>
    );
  }

  if (drillStatus === "completed" && !showSuccess) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-8"
            data-testid="drill-complete-screen"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Drill Complete!</h2>
            <p className="text-muted-foreground mb-6">
              You've completed all {questions.length} questions in "{drill.title}"
            </p>
            <Button onClick={onExit} className="bg-green-500 hover:bg-green-600" data-testid="drill-complete-home-btn">
              Return to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="drill-mode">
      <div className="max-w-7xl mx-auto">
        {/* Admin Preview Banner */}
        {drill.is_admin_preview && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-lg text-center"
          >
            <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">
              👁️ Preview Mode — Progress and points will not be saved
            </span>
          </motion.div>
        )}
        
        {/* Drill Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">
              {drill.is_admin_preview ? "Drill Preview" : "Coding Drill"}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
            <span className="text-orange-500">{drill.title}</span>
            {isBlockMode(drill.mode || currentQuestion?.mode) && (
              <span className={`ml-2 inline-flex items-center gap-1 rounded-full border text-xs font-medium px-2 py-0.5 align-middle ${MODE_META[normalizeMode(drill.mode || currentQuestion?.mode)].badge}`} data-testid="drill-mode-blocks-badge">
                <Blocks className="w-3 h-3" /> {modeLabel(drill.mode || currentQuestion?.mode)}
              </span>
            )}
          </h1>
          
          {/* Progress Bar */}
          <div className="max-w-md mx-auto mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Drill Progress</span>
              <span className="font-medium text-orange-500">
                {completedCount} / {questions.length}
              </span>
            </div>
            <Progress 
              value={(completedCount / questions.length) * 100} 
              className="h-2 [&>div]:bg-orange-500" 
            />
          </div>
          
          {/* Admin Preview Navigation - Back/Forward buttons */}
          {drill.is_admin_preview && (
            <div className="flex items-center justify-center gap-4 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (currentIndex > 0) {
                    setCurrentIndex(currentIndex - 1);
                    setTimeRemaining(DRILL_TIMER_SECONDS);
                    setTimerActive(true);
                  }
                }}
                disabled={currentIndex === 0}
                className="gap-1"
                data-testid="drill-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <span className="text-sm text-muted-foreground">
                Question {currentIndex + 1} of {questions.length}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (currentIndex < questions.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                    setTimeRemaining(DRILL_TIMER_SECONDS);
                    setTimerActive(true);
                  }
                }}
                disabled={currentIndex === questions.length - 1}
                className="gap-1"
                data-testid="drill-next-btn"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Question indicator */}
          <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
            {questions.map((q, idx) => (
              <button
                key={q.question_id}
                onClick={() => {
                  // Allow clicking in admin preview mode
                  if (drill.is_admin_preview) {
                    setCurrentIndex(idx);
                    setTimeRemaining(DRILL_TIMER_SECONDS);
                    setTimerActive(true);
                  }
                }}
                disabled={!drill.is_admin_preview && idx > currentIndex}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  idx < completedCount
                    ? "bg-green-500 text-white"
                    : idx === currentIndex
                    ? "bg-orange-500 text-white ring-2 ring-orange-300"
                    : "bg-muted text-muted-foreground"
                } ${drill.is_admin_preview ? "cursor-pointer hover:ring-2 hover:ring-orange-200" : ""}`}
                data-testid={`drill-question-${idx}`}
              >
                {idx < completedCount ? (
                  <CheckCircle className="w-4 h-4" />
                ) : !drill.is_admin_preview && idx > currentIndex ? (
                  <Lock className="w-3 h-3" />
                ) : (
                  idx + 1
                )}
              </button>
            ))}
          </div>
          
          {/* Stop Drill Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleStopDrill}
            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
            data-testid="stop-drill-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Stop Drill
          </Button>
        </motion.div>

        {/* Question and Code Editor */}
        {currentQuestion && (
          <div
            className={isBlockMode(currentQuestion.mode) ? "flex flex-col gap-6" : "grid grid-cols-1 lg:grid-cols-2 gap-6"}
            data-testid={isBlockMode(currentQuestion.mode) ? "question-layout-stacked" : "question-layout-split"}
          >
            {/* Question Card - No reveal answer in drill mode; Blocks questions stack above a full-width workspace */}
            <div>
              <QuestionCard 
                question={currentQuestion} 
                showRevealAnswer={false}
                compact={isBlockMode(currentQuestion.mode)}
              />
            </div>
            
            {/* Code Editor */}
            <div className="space-y-4">
              {/* Timer */}
              <div className="flex justify-end">
                <TimerDisplay
                  timeRemaining={timeRemaining}
                  maxTime={DRILL_TIMER_SECONDS}
                  label="Blitz Bonus"
                />
              </div>
              
              <CodeWorkspace
                key={currentQuestion.question_id}
                question={currentQuestion}
                questionId={currentQuestion.question_id}
                exampleInput={currentQuestion.example_input}
                onCorrect={handleCorrectAnswer}
                timeRemaining={timeRemaining}
                isDrillMode={true}
                drillId={drill.drill_id}
                classId={drill.class_id}
                layout={isBlockMode(currentQuestion.mode) ? "wide" : "split"}
              />
            </div>
          </div>
        )}
      </div>

      {/* Drill Success Modal */}
      {showSuccess && lastResult && (
        <DrillSuccessModal
          isOpen={showSuccess}
          onNextQuestion={handleSuccessClose}
          onReturnHome={() => {
            setShowSuccess(false);
            if (onDrillComplete) {
              onDrillComplete();
            }
            onExit();
          }}
          pointsEarned={lastResult.total_points}
          blitzBonus={lastResult.blitz_bonus}
          streakBonus={lastResult.streak_bonus}
          gemBonus={lastResult.gem_bonus}
          streakCount={lastResult.streak_count}
          isDrillComplete={lastResult.is_drill_complete}
          questionsCompleted={completedCount}
          totalQuestions={questions.length}
          drillTitle={drill.title}
        />
      )}
    </div>
  );
}
